// src/pages/EnqueteNutrition.jsx
import React, { useState, useEffect, useMemo } from 'react';
import NavigationBar from '../navbar/NavigationBar';
import DownloadIcon from '@mui/icons-material/Download';
import {
  Container,
  Typography,
  Grid,
  Card,
  Button,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  CardMedia,
} from '@mui/material';
import { styled, ThemeProvider, createTheme } from '@mui/material/styles';
import Footer from '../navbar/Footer';
import moment from 'moment';
import { getPublications } from '../services/publicationService';

function EnqueteNutrition() {
  const [visibleCount, setVisibleCount] = useState(2);
  const [selectedYear, setSelectedYear] = useState('Tous');
  const [bulletinsData, setBulletinsData] = useState([]);
  const [filteredBulletins, setFilteredBulletins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Chargement des données depuis l'API
  useEffect(() => {
    setLoading(true);
    getPublications({ type: 'ren', perPage: 1000 })
      .then((res) => {
        if (res.error) {
          setError(res.error);
        } else {
          // Mapper les données de l'API vers la structure attendue
          const mapped = res.data.map((item) => ({
            title: item.title,
            link: item.document_url,
            image: item.image_url,
            description: item.description,
            date: new Date(item.date), // utilisé pour le tri et le filtre
          }));
          // Trier par date décroissante (plus récent en premier)
          const sorted = mapped.sort((a, b) => b.date - a.date);
          setBulletinsData(sorted);
          setFilteredBulletins(sorted);
        }
      })
      .catch((err) => {
        setError('Erreur lors du chargement des données.');
        console.error(err);
      })
      .finally(() => setLoading(false));
  }, []);

  const handleShowMore = () => {
    setVisibleCount((prevCount) => prevCount + 2);
  };

  const handleYearChange = (event) => {
    const year = event.target.value;
    setSelectedYear(year);
    let filtered;
    if (year === 'Tous') {
      filtered = [...bulletinsData];
    } else {
      filtered = bulletinsData.filter((bulletin) => {
        const bulletinYear = bulletin.date.getFullYear().toString();
        return bulletinYear === year;
      });
    }
    // Trier par date décroissante
    filtered.sort((a, b) => b.date - a.date);
    setFilteredBulletins(filtered);
    setVisibleCount(2); // réinitialiser l'affichage
  };

  const StyledSelect = styled(Select)(({ theme }) => ({
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.main,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.light,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.dark,
    },
  }));

  const theme = createTheme();

  // Années disponibles à partir des données chargées
  const availableYears = useMemo(() => {
    const years = bulletinsData.map((bulletin) => bulletin.date.getFullYear());
    return [...new Set(years)].sort((a, b) => b - a);
  }, [bulletinsData]);

  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <div style={{ backgroundColor: '#f0f4f8', paddingBottom: '20px' }}>
          <NavigationBar />
          <Container maxWidth="lg" style={{ paddingTop: '54px', paddingInline: '20px' }}>
            <Typography variant="h4" align="justify" gutterBottom style={{ color: '#388E3C' }}>
              Rapports des Enquêtes Nutrition
            </Typography>
            <Divider style={{ margin: '20px 0' }} />
            <Typography variant="body1">Chargement des données...</Typography>
          </Container>
          <Footer />
        </div>
      </ThemeProvider>
    );
  }

  if (error) {
    return (
      <ThemeProvider theme={theme}>
        <div style={{ backgroundColor: '#f0f4f8', paddingBottom: '20px' }}>
          <NavigationBar />
          <Container maxWidth="lg" style={{ paddingTop: '54px', paddingInline: '20px' }}>
            <Typography variant="h4" align="justify" gutterBottom style={{ color: '#388E3C' }}>
              Rapports des Enquêtes Nutrition
            </Typography>
            <Divider style={{ margin: '20px 0' }} />
            <Typography variant="body1" color="error">{error}</Typography>
          </Container>
          <Footer />
        </div>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <div style={{ backgroundColor: '#f0f4f8', paddingBottom: '20px' }}>
        <NavigationBar />
        <Container maxWidth="lg" style={{ paddingTop: '54px', paddingInline: '20px' }}>
          <Typography variant="h4" align="justify" gutterBottom style={{ color: '#388E3C' }}>
            Rapports des Enquêtes Nutrition
          </Typography>
          <Divider style={{ margin: '20px 0' }} />
          <Typography variant="body1" align="left" gutterBottom>
            Les enquêtes nutritionnelles sont des outils essentiels pour évaluer l'état nutritionnel de la population burundaise. Les données collectées permettent de comprendre les défis nutritionnels, d'identifier les groupes vulnérables et de développer des politiques éclairées pour améliorer la nutrition et la santé publique. Sur cette page, vous trouverez les rapports des enquêtes nutritionnelles réalisées par l'Institut National de la Statistique du Burundi, offrant des informations détaillées sur la situation nutritionnelle du pays.
          </Typography>

          <FormControl fullWidth margin="normal" style={{ marginLeft: '0px', width: '300px', marginBottom: '20px' }}>
            <InputLabel id="year-select-label">Sélectionnez une année de publication</InputLabel>
            <StyledSelect
              labelId="year-select-label"
              id="year-select"
              value={selectedYear}
              onChange={handleYearChange}
              label="Sélectionnez une année de publication"
              variant="outlined"
            >
              <MenuItem value="Tous">Tous les rapports</MenuItem>
              {availableYears.map((year) => (
                <MenuItem key={year} value={year.toString()}>
                  {year}
                </MenuItem>
              ))}
            </StyledSelect>
          </FormControl>

          <Grid container spacing={1} style={{ marginTop: '10px' }}>
            {filteredBulletins.slice(0, visibleCount).map((bulletin, index) => (
              <Grid item xs={12} sm={6} md={12} key={index}>
                <Card elevation={3} style={{ borderRadius: '12px', overflow: 'hidden', display: 'flex', flexDirection: 'column', height: '100%' }}>
                  <Box sx={{ display: 'flex' }}>
                    <CardMedia
                      component="img"
                      sx={{ width: 91, height: 140, m: 2, cursor: 'pointer' }}
                      image={bulletin.image}
                      alt={bulletin.title}
                      onClick={() => window.open(bulletin.link, '_blank')}
                    />
                    <Box sx={{ display: 'flex', flexDirection: 'column', pl: 2, pt: 2 }}>
                      <Typography variant="h6" component="div" style={{ fontWeight: 'bold', marginBottom: '10px', color: '#388E3C' }}>
                        <a
                          href={bulletin.link}
                          style={{ textDecoration: 'none', color: 'inherit' }}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <DownloadIcon /> {bulletin.title}
                        </a>
                      </Typography>
                      <Typography variant="body2" component="div" style={{ color: '#555', marginBottom: '10px' }}>
                        {bulletin.description}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Publié en {moment(bulletin.date).format('MMMM YYYY')}
                      </Typography>
                    </Box>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>

          {visibleCount < filteredBulletins.length && (
            <Button
              onClick={handleShowMore}
              style={{ marginTop: '20px', backgroundColor: '#388E3C', color: 'white' }}
            >
              Plus de rapports
            </Button>
          )}
        </Container>
        <br />
        <br />
        <Footer />
      </div>
    </ThemeProvider>
  );
}

export default EnqueteNutrition;